/* eslint-disable prefer-arrow/prefer-arrow-functions */

import { Predicate } from './ts-util';

type CompoundEvaluator<T> = (a: T, b: T) => number[];
export type PropertySelector<T, Result> = (source: T) => Result;

function getResultMultiplier(ascending: boolean): number {
  return ascending ? 1 : -1;
}

export function compoundSort<T>(array: T[], ascending: boolean, evaluator: CompoundEvaluator<T>): T[] {
  const resultMultiplier = getResultMultiplier(ascending);

  return array.sort((a, b) => {
    const evaluation = evaluator(a, b);

    for (const evaluationResult of evaluation) {
      if (evaluationResult !== 0) {
        return resultMultiplier * evaluationResult;
      }
    }

    return 0;
  });
}

export function compoundSortAscending<T>(array: T[], evaluator: CompoundEvaluator<T>): T[] {
  return compoundSort(array, true, evaluator);
}

export function compoundSortDescending<T>(array: T[], evaluator: CompoundEvaluator<T>): T[] {
  return compoundSort(array, false, evaluator);
}

export function booleanCompare(a: boolean, b: boolean): number {
  if (a === true && b !== true) {
    return -1;
  }

  if (a === b) {
    return 0;
  }

  return 1;
}

export function sortBy<T, S>(array: T[], propertySelector: PropertySelector<T, S>, ascending = true): T[] {
  const resultMultiplier = getResultMultiplier(ascending);
  return array.sort((a, b) => {
    const propA = propertySelector(a);
    const propB = propertySelector(b);

    return resultMultiplier * (propA > propB ? 1 : propA === propB ? 0 : -1);
  });
}

export function sortByAscending<T, S>(array: T[], propertySelector: PropertySelector<T, S>): T[] {
  return sortBy(array, propertySelector, true);
}

export function sortByDescending<T, S>(array: T[], propertySelector: PropertySelector<T, S>): T[] {
  return sortBy(array, propertySelector, false);
}

export function removeMatching<T>(array: T[], predicate: Predicate<T>): boolean {
  const idx = array.indexOf(array.find((it) => predicate(it)));
  if (idx !== -1) {
    array.splice(idx, 1);
    return true;
  }
  return false;
}

export function remove<T>(array: T[], element: T): boolean {
  const idx = array.indexOf(element);
  if (idx !== -1) {
    array.splice(idx, 1);
    return true;
  }
  return false;
}

export function groupBy<T, S>(array: T[], propertySelector: PropertySelector<T, S>): Map<S, T[]> {
  const result = new Map<S, T[]>();

  for (const element of array) {
    const groupByProperty = propertySelector(element);

    if (result.get(groupByProperty)) {
      result.get(groupByProperty).push(element);
    } else {
      result.set(groupByProperty, [element]);
    }
  }

  return result;
}

export function uniqueBy<T, S>(array: T[], identitySelector: (element: T) => S): T[] {
  const ret = [];
  array.forEach((it) => {
    if (!ret.find((it2) => identitySelector(it) === identitySelector(it2))) {
      ret.push(it);
    }
  });
  return ret;
}

// Code brought over from https://github.com/behnammodi/polyfill/blob/master/array.polyfill.js
export function flattenByOne<T>(array: T[][]): T[] {
  const result: T[] = [];
  const forEach = result.forEach;

  const flatDeep = function (arr: T[][], _depth: number) {
    forEach.call(arr, function (val: T) {
      if (_depth > 0 && Array.isArray(val)) {
        flatDeep(val, _depth - 1);
      } else {
        result.push(val);
      }
    });
  };

  flatDeep(array, 1);
  return result;
}
